import { ThemeName } from "@components/atoms/Themed"
import { CaseStudyCarousel } from "@components/site/CaseStudyCarousel"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useBlockContext } from "eddev/blocks"
import { ResponsiveImageFragment } from "../../types.graphql"
import { ScrollReveal, Transition } from "@components/motion/Transitioned"
import { FlowgramImageType } from "@components/site/FlowgramImage"

export const meta: BlockMeta = {
  title: "Case Study List",
  icon: "block-default",
  tags: ["#page"],
}

type ItemsType = {
  title: string
  subtitle?: string
  flowgram?: FlowgramImageType
  uri: string
  featuredImages: (ResponsiveImageFragment | null)[]
  pageTheme?: ThemeName | null
}

export default defineBlock("content/case-study-list", (props) => {
  const ctx = useBlockContext()

  let themeOverride = props.type === "category" ? props.ccategory?.theme : undefined

  const onWorkPage = ctx?.parents.find((p) => p[0] === "acf/work-page-work-listing")

  let items: ItemsType[] = []
  if (props.type === "latest") {
    items =
      props.caseStudies?.nodes?.map((i) => ({
        title: i?.title!,
        subtitle: i.pageHeaderCaseStudy?.subtitle || undefined,
        flowgram: i.pageHeaderCaseStudy?.flowgramImage || undefined,
        uri: i?.uri!,
        featuredImages: i?.pageHeaderCaseStudy?.featuredImages || [],
        pageTheme: i?.pageTheme!,
      })) || []
  }
  if (props.type === "selected") {
    items =
      props.selectedContent?.map((i) => ({
        title: i?.title!,
        subtitle: i?.pageHeaderCaseStudy?.subtitle || undefined,
        flowgram: i?.pageHeaderCaseStudy?.flowgramImage || undefined,
        uri: i?.uri!,
        featuredImages: i?.pageHeaderCaseStudy?.featuredImages || [],
        pageTheme: i?.pageTheme!,
      })) || []
  }
  if (props.type === "next") {
    items =
      props.caseStudy?.siblings
        ?.filter((i) => i?.type === "next")
        .map((i) => ({
          title: i?.title!,
          subtitle: i?.subtitle || undefined,
          flowgram: i?.flowgram || undefined,
          uri: i?.uri!,
          featuredImages: i?.featuredImages!,
          pageTheme: i?.pageTheme!,
        })) || []
  }
  if (props.type === "category") {
    items =
      props.ccategory?.contentNodes?.nodes
        ?.filter((c) => "title" in c)
        .map((i) => ({
          title: i?.title!,
          subtitle: i?.pageHeaderCaseStudy?.subtitle || undefined,
          flowgram: i?.pageHeaderCaseStudy?.flowgramImage || undefined,
          uri: i?.uri!,
          featuredImages: i?.pageHeaderCaseStudy?.featuredImages || [],
          pageTheme: props.ccategory?.theme! || i?.pageTheme,
        })) || []
  }

  return (
    <div className={cn("w-grid mx-auto flex flex-col gap-7", onWorkPage ? "mb-10" : "my-10")}>
      <ScrollReveal>
        {onWorkPage ? (
          // Header which is shown on the work page, with larger title
          <div className="grid-auto mb-4 gap-y-4">
            <div className="col-span-12 md:col-span-5">
              <EditableText
                as="h3"
                store="title"
                className="type-title-m md:type-title-l text-pretty w-[9em]"
                placeholder="Enter a title"
                data-transition
              />
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-8">
              <EditableText
                as="p"
                store="description"
                className="type-title-s text-pretty max-w-[18em]"
                placeholder="Enter a description"
                data-transition
              />
            </div>
          </div>
        ) : (
          // Smaller, optional title
          <EditableText as="p" store="title" className="type-label-l md:type-label-xl" placeholder="Optional title" />
        )}
      </ScrollReveal>
      <div className="w-full flex flex-col gap-4">
        {items.map((item, index) => (
          <CaseStudyCarousel
            key={item.uri}
            title={item.title!}
            subtitle={item.subtitle}
            flowgram={item.flowgram}
            uri={item.uri!}
            images={item.featuredImages! || []}
            theme={themeOverride ?? item.pageTheme!}
          />
        ))}
        {env.admin && items.length === 0 && (
          <div className="w-full p-8 rounded-lg bg-grey flex items-center justify-center type-label-m">
            (Next Case Study shown outside of admin mode)
          </div>
        )}
      </div>
    </div>
  )
})
